class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      gridContainer.style.minHeight = gridContainer.offsetHeight + 'px';
      gridContainer.querySelector('.collection')?.classList.add('loading');
    }
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    const pathname = event && event.detail && event.detail.pathname ? event.detail.pathname : window.location.pathname;

    sections.forEach((section) => {
      const url = `${pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams, pathname);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      gridContainer.style.minHeight = gridContainer.offsetHeight + 'px';
      gridContainer.querySelector('.collection')?.classList.add('loading');
    }
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const container = document.getElementById('ProductGridContainer');
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const newGrid = parsedHTML.getElementById('ProductGridContainer');

    if (newGrid && container) {
      container.innerHTML = newGrid.innerHTML;
      container.style.minHeight = '';

      container
        .querySelectorAll('.scroll-trigger')
        .forEach((element) => {
          element.classList.add('scroll-trigger--cancel');
        });

      FacetFiltersForm.executeScripts(container);
      if (typeof window.__NCInitCards === 'function') window.__NCInitCards();
    }
  }

  static executeScripts(container) {
    container.querySelectorAll('script').forEach((oldScript) => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attr) => newScript.setAttribute(attr.name, attr.value));
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const countElement = parsedHTML.getElementById('ProductCount');
    if (!countElement) return;

    const count = countElement.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          const parentElement = document.getElementById(elementToRender.parentElement.id);
          if (parentElement) {
            const filterElement = parentElement.querySelector('.js-filter');
            if (filterElement) {
              filterElement.before(elementToRender);
            }
          }
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && event && event.target.closest('.js-filter')) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, document.getElementById(closestJSFilterID));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const targetElement = document.querySelector(selector);
      if (targetElement) {
        targetElement.style.minHeight = targetElement.offsetHeight + 'px';
        targetElement.innerHTML = activeFacetsElement.innerHTML;
        FacetFiltersForm.executeScripts(targetElement);
        requestAnimationFrame(() => {
          targetElement.style.minHeight = '';
        });
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const targetElement = document.querySelector(selector);
      if (targetElement) {
        targetElement.innerHTML = html.querySelector(selector).innerHTML;
        FacetFiltersForm.executeScripts(targetElement);
      }
    });

    const mobileFacetForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileFacetForm && mobileFacetForm.closest('menu-drawer')) {
      mobileFacetForm.closest('menu-drawer').bindEvents();
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams, pathname = window.location.pathname) {
    history.pushState({ searchParams }, '', `${pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

// Delegated listener for subcategory links to handle AJAX filtering
document.body.addEventListener('click', (event) => {
  const subcatLink = event.target.closest('.collection-subcategories__link');
  if (!subcatLink) return;

  event.preventDefault();

  if (subcatLink.classList.contains('is-loading')) return;

  const url = new URL(subcatLink.href, window.location.origin);
  const searchParams = url.search.slice(1);
  const pathname = url.pathname;

  // Add loading state
  subcatLink.classList.add('is-loading');

  // Also add loading to grid
  const gridContainer = document.getElementById('ProductGridContainer');
  if (gridContainer) gridContainer.querySelector('.collection')?.classList.add('loading');

  if (window.FacetFiltersForm) {
    FacetFiltersForm.renderPage(searchParams, {
      target: subcatLink,
      detail: { pathname: pathname },
      preventDefault: () => { }
    });

    // Update active class immediately for better UX
    const subcatGrid = subcatLink.closest('.collection-subcategories-grid');
    if (subcatGrid) {
      subcatGrid.querySelectorAll('.collection-subcategories__link').forEach(link => link.classList.remove('is-active'));
      subcatLink.classList.add('is-active');
    }
  } else {
    window.location.href = subcatLink.href;
  }
});

// Reset loading states after AJAX render
document.addEventListener('DOMContentLoaded', () => {
  const observer = new MutationObserver(() => {
    document.querySelectorAll('.collection-subcategories__link.is-loading').forEach(link => {
      link.classList.remove('is-loading');
    });
  });

  const gridContainer = document.getElementById('ProductGridContainer');
  if (gridContainer) {
    observer.observe(gridContainer, { childList: true, subtree: true });
  }
});

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.sliderInputs = this.querySelectorAll('input[type="range"]');
    this.numberInputs = this.querySelectorAll('input[type="number"]');
    this.trackActive = this.querySelector('.price-range-slider__track-active');

    if (this.sliderInputs.length && this.numberInputs.length) {
      this.sliderInputs.forEach((input) => {
        input.addEventListener('input', this.onSliderInput.bind(this));
        input.addEventListener('change', this.onSliderChange.bind(this));
      });
      this.numberInputs.forEach((input) => {
        input.addEventListener('input', this.onNumberInput.bind(this));
        input.addEventListener('change', this.onNumberChange.bind(this));
        input.addEventListener('keydown', this.onKeyDown.bind(this));
      });
      this.updateSliderTrack();
    } else {
      // Fallback to original logic if we are missing our custom DOM
      this.querySelectorAll('input').forEach((element) => {
        element.addEventListener('change', this.onRangeChange.bind(this));
        element.addEventListener('keydown', this.onKeyDown.bind(this));
      });
      this.setMinAndMaxValues();
    }
  }

  onSliderInput(event) {
    let minVal = parseInt(this.sliderInputs[0].value) || 0;
    let maxVal = parseInt(this.sliderInputs[1].value) || 0;

    // Swap if crossed
    if (minVal > maxVal) {
      if (event.target === this.sliderInputs[0]) {
        this.sliderInputs[0].value = maxVal;
        minVal = maxVal;
      } else {
        this.sliderInputs[1].value = minVal;
        maxVal = minVal;
      }
    }

    this.numberInputs[0].value = minVal === 0 ? '' : minVal;

    // Only blank out max if it equals the absolute max possible
    const maxPossible = parseInt(this.sliderInputs[1].max);
    this.numberInputs[1].value = maxVal === maxPossible ? maxPossible : maxVal;

    this.updateSliderTrack();
  }

  onSliderChange(event) {
    this.numberInputs[0].dispatchEvent(new Event('change', { bubbles: true }));
  }

  onNumberInput(event) {
    let minVal = parseInt(this.numberInputs[0].value) || 0;
    let maxVal = parseInt(this.numberInputs[1].value) || parseInt(this.numberInputs[1].max);

    this.sliderInputs[0].value = minVal;
    this.sliderInputs[1].value = maxVal;
    this.updateSliderTrack();
  }

  onNumberChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.onNumberInput(event);
  }

  updateSliderTrack() {
    const minVal = parseInt(this.sliderInputs[0].value);
    const maxVal = parseInt(this.sliderInputs[1].value);
    const maxPossible = parseInt(this.sliderInputs[0].max);

    if (maxPossible > 0 && this.trackActive) {
      const leftPercent = (minVal / maxPossible) * 100;
      const rightPercent = 100 - (maxVal / maxPossible) * 100;

      this.trackActive.style.left = `${leftPercent}%`;
      this.trackActive.style.right = `${rightPercent}%`;
      this.trackActive.style.backgroundColor = '#000';
    }
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

/* =======================================================
   ✅ FINAL STICKY FACETS — SUBCATEGORY DRIVEN (NO GAP)
   Header & Subcategory handled by StickyHeader ONLY
========================================================== */
(() => {
  const facets = document.querySelector(".facets-wrapper");
  const subcategory = document.querySelector(".subcategory-navigation");
  const header = document.querySelector(".section-header");

  // Guard: this sticky logic only applies on pages that have ALL three elements
  // (e.g. collection pages). Search / other pages won't have subcategory nav.
  if (!facets || !subcategory || !header) return;

  const subcategoryNav = subcategory.querySelector(".subcategory-navigation__inner");

  let isDesktop = window.matchMedia("(min-width: 750px)").matches;
  let ticking = false;

  // 🔒 Capture document position ONCE
  const subcategoryDocTop =
    subcategory.getBoundingClientRect().top + window.scrollY;

  const updateSticky = () => {
    if (!isDesktop) return;

    const scrollTop = window.scrollY || window.pageYOffset;

    let triggerPoint = 0;
    let top = 0;


    // ✅ CONDITION 1: subcategory INNER EXISTS
    if (subcategoryNav !== null && subcategoryNav.offsetHeight > 0) {

      // Trigger after subcategory finishes
      const subDocTop =
        subcategory.offsetTop + subcategory.offsetHeight;

      triggerPoint = subDocTop;

      // Top = header + full subcategory
      top = header.offsetHeight + subcategory.offsetHeight;

    }
    // ✅ CONDITION 2: NO inner nav → header only
    else {

      triggerPoint =
        header.offsetTop;

      top = header.offsetHeight;
    }

    if (scrollTop >= triggerPoint) {
      facets.classList.add("is-fixed"); // use custom class
      facets.style.top = `${top - 1}px`;
    } else {
      facets.classList.remove("is-fixed");
      facets.style.top = "";
    }
  };



  const rafScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      updateSticky();
      ticking = false;
    });
  };

  window.addEventListener("scroll", rafScroll, { passive: true });
  window.addEventListener("resize", () => {
    isDesktop = window.matchMedia("(min-width: 750px)").matches;
    updateSticky();
  });

  // 🔥 After AJAX filter update
  document.addEventListener("collection:updated", () => {
    requestAnimationFrame(updateSticky);
  });

  // initial
  requestAnimationFrame(updateSticky);
})();

